import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PageHeader } from "@ant-design/pro-components";
import {
  ConfigProvider,
  Layout,
  Row,
  Col,
  Empty,
  Space,
  Modal,
  notification,
  Spin,
  Card,
  Breadcrumb,
  Statistic,
  Result,
  Button,
  Progress,
  Alert,
} from "antd";
import { ProTable, BetaSchemaForm } from "@ant-design/pro-components";
import {
  ExclamationCircleOutlined,
  FileSyncOutlined,
  FileProtectOutlined,
  FileExclamationOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import moment from "moment";
import ptBRIntl from "antd/lib/locale/pt_BR";
import uuid from "react-uuid";
import "moment/locale/pt-br";
import "reactjs-popup/dist/index.css";
import "./styles.scss";

/**
 * Actions
 */
import {
  addRefFieldsFormData,
  deleteAllFormData,
  deleteAllRefFieldsFormData,
  fetchImportFormData,
} from "hooks/dataOfForm";
import { importApi } from "services/actions/import";
import { addRecords } from "auth/actions/objActions";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";

/**
 * Utils
 */
import buildColumns, {
  buildSchemaColumns,
  getLookupData,
} from "components/Utils/formColumns";
import buildTableData from "components/Utils/formData";
import sleep from "components/Utils/sleep";
import Notification from "components/Notifications";
import { showNotification } from "hooks/notification";
import mountRecords from "components/Utils/buildRecords";
import removeUndefined from "components/Utils/removeUndefined";
import { getFieldValueForTable } from "components/Utils/getFieldValue";
import { enforceParentChildOrder } from "utils/order-parent-child-fields";
import { defaultTablePagination } from "utils/table-pagination";
import socketInstance from "../../../../services/socket";
import { trackActivity } from "services/helper/activity";
import { activityAPI } from "services/actions/activity";

/**
 * Misc
 */
const { confirm } = Modal;
const buildRoutes = (formLabel) => [
  {
    breadcrumbName: "Formulários",
    href: "/formularios/todos",
  },
  {
    breadcrumbName: "importação",
  },
  {
    breadcrumbName: formLabel || "formulário",
  },
  {
    breadcrumbName: "status",
  },
];

const normalizeValue = (value) => {
  if (value === undefined || value === null) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed === "0") return null;
    if (trimmed.toLowerCase() === "nan") return null;
    return trimmed;
  }

  if (typeof value === "number" && value === 0) return null;

  return value;
};

const normalizeMultiValue = (value) => {
  if (!value) return [];

  // vindo do form (array de objetos)
  if (Array.isArray(value)) {
    return value
      .map(v => v?.value)
      .filter(v => v !== null && v !== undefined && v !== '')
      .map(String)
      .sort();
  }

  // vindo do banco (string "a, b, c")
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .sort();
  }

  return [String(value)];
};

const toNumber = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num : 0;
};

const hasProgressShape = (payload) =>
  !!payload &&
  typeof payload === "object" &&
  [
    "queueId",
    "total",
    "totalRecords",
    "totalProcessed",
    "successCount",
    "errorCount",
    "status",
    "progress",
    "waiting",
    "processedRecords",
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));

const extractProgressPayload = (rawPayload) => {
  if (rawPayload === null || rawPayload === undefined) return null;

  let payload = rawPayload;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (error) {
      return null;
    }
  }

  if (payload && typeof payload === "object" && payload.value !== undefined) {
    const value = payload.value;
    if (typeof value === "string") {
      try {
        payload = JSON.parse(value);
      } catch (error) {
        payload = value;
      }
    } else {
      payload = value;
    }
  }

  if (
    payload &&
    typeof payload === "object" &&
    payload.payload &&
    hasProgressShape(payload.payload)
  ) {
    payload = payload.payload;
  }

  return hasProgressShape(payload) ? payload : null;
};

const normalizeProgressPayload = (payload, fallbackQueueId = null) => {
  const normalizedRawPayload = extractProgressPayload(payload);
  if (!normalizedRawPayload || typeof normalizedRawPayload !== "object") return null;

  const normalizedQueueId = normalizedRawPayload.queueId ?? fallbackQueueId ?? null;
  const successCount = toNumber(
    normalizedRawPayload.successCount ?? normalizedRawPayload.processedRecords
  );
  const errorCount = toNumber(normalizedRawPayload.errorCount);
  const totalRecords = toNumber(
    normalizedRawPayload.totalRecords ??
    normalizedRawPayload.total ??
    normalizedRawPayload.waiting
  );
  const totalProcessed = toNumber(
    normalizedRawPayload.totalProcessed ?? successCount + errorCount
  );
  const status =
    normalizedRawPayload.status ||
    (totalRecords > 0 && totalProcessed >= totalRecords
      ? "finished"
      : "processing");

  return {
    queueId: normalizedQueueId,
    successCount,
    errorCount,
    totalRecords,
    totalProcessed,
    status,
  };
};

function ImportStatus(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const [queue, setQueue] = useState(null);
  const [data, setData] = useState({
    total: 0,
    successCount: 0,
    errorCount: 0
  })
  const hasErrors = data.errorCount > 0;
  const [errorsLoading, setErrorsLoading] = useState(false);
  const errorFetchTimerRef = useRef(null);
  const errorFetchAttemptsRef = useRef(0);
  const errorFetchInFlightRef = useRef(false);
  const recordsRef = useRef([]);
  const [queueInfo, setQueueInfo] = useState(null);

  const [timeDiff, setTimeDiff] = useState('');
  const intervalRef = useRef(null);

  // 
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    recordsRef.current = props.formData.records || [];
  }, [props.formData.records]);

  // Poll queue-info when import is in queue/pending status
  useEffect(() => {
    const isWaiting = queue && (queue.status === 'queue' || queue.status === 'pending');
    if (!isWaiting) {
      setQueueInfo(null);
      return;
    }

    const fetchQueueInfo = async () => {
      try {
        const info = await importApi.getQueueInfo();
        if (info && info.success) {
          setQueueInfo(info);
        }
      } catch (e) {
        // silently ignore
      }
    };

    fetchQueueInfo();
    const queueInfoInterval = setInterval(fetchQueueInfo, 5000);

    return () => clearInterval(queueInfoInterval);
  }, [queue?.status]);

  useEffect(() => {
    const calculateTimeDiff = () => {
      // Use queue.createdAt as fallback if startTime is missing
      const start = queue?.startTime || queue?.createdAt;
      // Convert start to moment object if it's a string
      const startTime = start ? moment(start) : null;

      let endTime = queue?.endTime;

      // If finished or error but no endTime, use updatedAt or now as fallback to stop timer
      if (!endTime && (queue?.status === "finished" || queue?.status === "error")) {
        endTime = queue?.updatedAt || new Date();
      }

      if (startTime && startTime.isValid()) {
        setTimeDiff(formatTime(startTime, endTime));
      } else {
        setTimeDiff('');
      }
    };

    // Initial calculation
    calculateTimeDiff();

    const isFinished = queue?.status === "finished" || queue?.status === "error";

    // If finished, no need for interval
    if (queue?.endTime || isFinished) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // If running (has start/createdAt but no end), set interval
    const start = queue?.startTime || queue?.createdAt;
    if (queue && start && !isFinished) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        calculateTimeDiff();
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [queue]);

  const name = queryParams.get("form");
  const currentForm = decodeURIComponent(name);

  // Tabela
  const tableRef = useRef();
  const orderedFormFields = useMemo(() => {
    const unique = _.uniqBy(props.formData.fields || [], "field").sort(
      (a, b) => a.index - b.index
    );
    return enforceParentChildOrder(unique);
  }, [props.formData.fields]);
  const columns = useMemo(
    () =>
      buildColumns(
        orderedFormFields.map((field) => field.field),
        orderedFormFields,
        props.formData.refFields
      ),
    [
      orderedFormFields,
      props.formData.refFields,
    ]
  );
  const [checkedRowsData, setCheckedRowsData] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);
  const [checkedRowsRef, setCheckedRowsRef] = useState({});

  // Converte os registros para a tabela apenas quando eles mudam.
  // Com importações grandes (10k+ erros), recalcular isso a cada render
  // congela a interface.
  const tableRows = useMemo(
    () => buildTableData(props.formData.records),
    [props.formData.records]
  );

  // Formulário
  const recordFormRef = useRef();
  const formRef = useRef();
  const [recordFormAction, setRecordFormAction] = useState("add");
  const recordFormColumns = useMemo(
    () =>
      buildSchemaColumns(
        orderedFormFields.map((item) => item.field),
        orderedFormFields,
        props.formData.refFields,
        checkedRowsRef,
        recordFormRef,
        recordFormAction,
        true,
        dispatch,
        currentForm
      ),
    [
      orderedFormFields,
      props.formData.refFields,
      checkedRowsRef,
    ]
  );

  let recordFormColumnsFinal;
  if (recordFormAction !== "multipleEdit") {
    recordFormColumnsFinal = recordFormColumns;
  } else {
    recordFormColumnsFinal = [
      ...recordFormColumns.map(field => {
        if (field.valueType && (field.valueType === "date" || field.valueType === "time") && field.initialValue) {
          field.initialValue = null;
        }

        if (_.isObject(field.formItemProps)) {
          if (field.formItemProps.hasOwnProperty('required')) {
            field.formItemProps.required = false;
          }
        }
        return field;
      }),
      ...[
        {
          key: "acceptEmptyValues",
          name: "acceptEmptyValues",
          title: "Considerar valores vazios para atualização de campos dos registros selecionado?",
          valueType: "switch"
        }
      ]
    ];

  }

  // Edição
  const [openForm, setOpenForm] = useState(false);
  const socketRef = useRef(null);
  const hasSocketUpdateRef = useRef(false);
  const finishHandledRef = useRef(false);

  useEffect(() => {
    hasSocketUpdateRef.current = false;
    finishHandledRef.current = false;
  }, [id]);

  const fetchData = useCallback(async ({ forceDataUpdate = false, source = "manual" } = {}) => {
    try {
      const detailsOfImport = await importApi.filesHistoryById(id);
      let item = detailsOfImport;
      if (detailsOfImport && detailsOfImport.message && Array.isArray(detailsOfImport.message)) {
        item = detailsOfImport.message[0];
      }

      if (item) {
        // Map new backend fields to legacy frontend expectations
        if (item.totalRecords !== undefined && item.waiting === undefined) {
          item.waiting = item.totalRecords;
          item.successCount =
            toNumber(item.processedRecords) - toNumber(item.errorCount);
        }

        // errorDocuments pode conter milhares de registros completos e ninguém
        // lê essa lista a partir de `queue` — manter só os metadados da fila.
        setQueue(_.omit(item, ["errorDocuments"]));

        // Legacy calculation logic seems to treat 'waiting' as Total? 
        // Or if 'waiting' is actually remaining, the logic `waiting - (error + success)` would be wrong.
        // Assuming 'waiting' meant Total based on socket logic `data.total - ...`.
        const waiting = toNumber(item.waiting);
        const errorCount = toNumber(item.errorCount);
        const successCount = toNumber(item.successCount);
        const totalCalc = waiting - (errorCount + successCount);

        if (forceDataUpdate || !hasSocketUpdateRef.current) {
          setData({
            total: totalCalc >= 0 ? totalCalc : 0,
            errorCount,
            successCount,
          });
        }
      }
    } catch (error) {
      dispatch(
        showNotification(
          "Não foi possível obter os detalhes da importação, atualize a página e tente novamente!",
          "error"
        )
      );
    }
  }, [id, dispatch]);

  const mountedRef = useRef(true);

  const initializeSocket = useCallback(async () => {
    if (!socketRef.current) {
      const socket = await socketInstance();
      if (!mountedRef.current) {
        socket.disconnect();
        return;
      }
      socketRef.current = socket;

      const handleProgress = async (rawPayload, fallbackQueueId = null) => {
        if (!mountedRef.current) return;
        const progressPayload = normalizeProgressPayload(rawPayload, fallbackQueueId);
        if (!progressPayload) return;
        if (
          progressPayload.queueId &&
          String(progressPayload.queueId) !== String(id)
        ) {
          return;
        }

        hasSocketUpdateRef.current = true;

        const total =
          progressPayload.totalRecords -
          (progressPayload.errorCount + progressPayload.successCount);

        setData({
          total: total >= 0 ? total : 0,
          errorCount: progressPayload.errorCount,
          successCount: progressPayload.successCount,
        });

        if (
          progressPayload.status === "finished" ||
          (progressPayload.totalRecords > 0 &&
            progressPayload.totalProcessed >= progressPayload.totalRecords)
        ) {
          // O backend emite mais de um evento com status "finished";
          // buscar os dados completos uma única vez.
          if (!finishHandledRef.current) {
            finishHandledRef.current = true;
            dispatch(fetchImportFormData(currentForm, id));
            fetchData({ source: "socket-finish" });
          }
        }
      };

      const progressEvents = _.uniq(
        _.compact([
          props.user?.sub ? `${props.user.sub}:importProgress` : null,
          props.user?.uuid ? `${props.user.uuid}:importProgress` : null,
          props.user?.id ? `${props.user.id}:importProgress` : null,
          `${id}:queueStatus`,
          `${id}:importProgress`,
          "importProgress",
          "import:progress",
          "queueStatus",
          "queue:status",
        ])
      );
      const progressEventSet = new Set(progressEvents);

      progressEvents.forEach((eventName) => {
        socketRef.current.on(eventName, (payload) =>
          handleProgress(payload, eventName === `${id}:queueStatus` ? id : null)
        );
      });

      socketRef.current.onAny((eventName, payload) => {
        if (!mountedRef.current) return;
        if (progressEventSet.has(eventName)) return;

        const candidate = extractProgressPayload(payload);
        if (!candidate) return;

        handleProgress(payload);
      });

      const joinTargets = _.uniq(
        _.compact(["import", id, props.user?.sub, props.user?.uuid, props.user?.id])
      );
      joinTargets.forEach((target) => {
        try {
          socketRef.current.emit("join", target);
        } catch (error) {
          // no-op: some websocket servers don't support join
        }
      });
    }
  }, [id, dispatch, currentForm, fetchData, props.user.sub, props.user.uuid, props.user.id]);

  useEffect(() => {
    mountedRef.current = true;
    initializeSocket();
    fetchData({ source: "mount" });
    return () => {
      mountedRef.current = false;
      if (errorFetchTimerRef.current) {
        clearInterval(errorFetchTimerRef.current);
        errorFetchTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [fetchData, initializeSocket]);

  useEffect(() => {
    const expectedErrors = queue ? Math.max(0, toNumber(data.errorCount) - (queue.clearDocs?.length || 0)) : toNumber(data.errorCount);

    if (!hasErrors || expectedErrors <= 0) {
      setErrorsLoading(false);
      errorFetchAttemptsRef.current = 0;
      if (errorFetchTimerRef.current) {
        clearInterval(errorFetchTimerRef.current);
        errorFetchTimerRef.current = null;
      }
      return;
    }

    setErrorsLoading(true);
    errorFetchAttemptsRef.current = 0;

    const refreshErrors = () => {
      const currentErrors = recordsRef.current.length;
      if (currentErrors >= expectedErrors) {
        setErrorsLoading(false);
        if (errorFetchTimerRef.current) {
          clearInterval(errorFetchTimerRef.current);
          errorFetchTimerRef.current = null;
        }
        return;
      }

      // Em importações grandes cada busca demora mais que o intervalo de 1s;
      // sem esse guard as requisições se acumulam e travam a página.
      if (errorFetchInFlightRef.current) return;

      errorFetchAttemptsRef.current += 1;
      errorFetchInFlightRef.current = true;
      Promise.resolve(dispatch(fetchImportFormData(currentForm, id))).finally(
        () => {
          errorFetchInFlightRef.current = false;
        }
      );

      if (errorFetchAttemptsRef.current >= 12) {
        setErrorsLoading(false);
        if (errorFetchTimerRef.current) {
          clearInterval(errorFetchTimerRef.current);
          errorFetchTimerRef.current = null;
        }
      }
    };

    refreshErrors();
    errorFetchTimerRef.current = setInterval(refreshErrors, 1000);

    return () => {
      if (errorFetchTimerRef.current) {
        clearInterval(errorFetchTimerRef.current);
        errorFetchTimerRef.current = null;
      }
    };
  }, [hasErrors, data.errorCount, currentForm, id, dispatch, queue]);

  /**
   * Remove registros
   */
  const deleteConfirm = () => {
    var ids = checkedRows.map((values) => values._id) || [];
    confirm({
      title: "Deseja excluir esses registros?",
      icon: <ExclamationCircleOutlined />,
      content: "Se confirmado essa ação é irreversível.",
      onOk() {
        importApi.filesHistoryErrorRemove({
          id,
          formName: name,
          errorIds: ids,
          action: "remove"
        })
          .then(async (res) => {
            var newRecords = res.message || [];
            if (newRecords.length > 0) {
              await sleep(800);
              importApi.filesHistoryById(id).then((items) => {
                setQueue(_.omit(items.message[0], ["errorDocuments"]));
              });
              await sleep(200);
              // Limpa itens selecionados na tabela
              notification["success"]({
                message: "Registros",
                description: "Removido com sucesso!",
              });
              setCheckedRows([]);
              // Atualiza a tabela
              if (tableRef.current) {
                tableRef.current.reloadAndRest();
              } else {
                window.location.reload();
              }
            } else {
              // Limpa itens selecionados na tabela
              notification["success"]({
                message: "Registros",
                description: "Removido com sucesso!",
              });
              setCheckedRows([]);
              await sleep(800);
              window.location.reload();
            }
          })
          .catch((err) =>
            dispatch(
              showNotification("Algo está errado, tente novamente!", "error")
            )
          );
      },
    });
  };

  const checkIfIsEmpty = (data) => {
    return data && _.every(data, (item) => _.isEmpty(item.id) && (_.isNil(item.value) || (!_.isNumber(item.value) && _.isEmpty(item.value.trim()))))
  }
  /**
   * Salva registros
   * @param {*} data
   * @param {*} action
   */
  const trySaveRecords = async (data, action) => {
    // Combina resultados buscando campos de retorno com nomes "duplicados"
    const keys = Object.keys(data);
    const uniqueKeys = new Set(keys);
    const haveDuplicateField = keys.length !== uniqueKeys.size;
    if (data && !_.isEmpty(data)) {
      if (haveDuplicateField) {
        dispatch(
          showNotification(
            "Não é permitido anexar campos de retornos com nomes duplicados. Por favor, remova os campos com nome duplicados dos campos de consulta.",
            "error"
          )
        );
      } else {
        if (action && action === "edit") {
          // Envia os registros para adição
          return addRecords({
            formName: currentForm,
            record: {
              ...data,
              _id: checkedRows[0]?._id,
            },
            createdBy: props.user.sub,
            amount: checkedRows.length
          })
            .then(async (res) => {
              if (res instanceof Error || (res && res.isAxiosError) || !res?.data?.length || !res?.data[0]) {
                notification["error"]({
                  message: "Erro ao salvar registros",
                  description: res?.response?.data?.message || res?.message || "Ocorreu um erro ao salvar os registros. Tente novamente.",
                });
                return;
              }

              notification["success"]({
                message: "Registros",
                description: res.message,
              });

              // CHANGE TRACKING LOGIC
              try {
                const originalRow = checkedRows[0];
                const newRecordId = res.data[0];

                const changes = Object.keys(data)
                  .map((field) => {
                    const newRaw = data[field];
                    const oldRaw = originalRow[field];

                    const isMulti = Array.isArray(newRaw);

                    const newValue = isMulti
                      ? normalizeMultiValue(newRaw)
                      : normalizeValue(newRaw?.[0]?.value ?? newRaw);

                    const oldValue = isMulti
                      ? normalizeMultiValue(oldRaw)
                      : normalizeValue(Array.isArray(oldRaw) ? oldRaw[0]?.value : oldRaw);

                    const isEqual = isMulti
                      ? JSON.stringify(newValue) === JSON.stringify(oldValue)
                      : newValue === oldValue;

                    if (isEqual) return null;

                    return {
                      id: newRecordId, // ID do novo registro criado
                      field,
                      value: isMulti ? newValue.join(', ') : newValue,
                      oldValue: isMulti ? oldValue.join(', ') : oldValue,
                    };
                  })
                  .filter(Boolean);

                if (changes.length > 0) {
                  const finalRecord = { ...originalRow }; // Começa com o original
                  // Atualiza com os novos valores
                  Object.keys(data).forEach((field) => {
                    const raw = data[field];
                    if (Array.isArray(raw)) {
                      finalRecord[field] = normalizeMultiValue(raw).join(', ');
                    } else {
                      const newValue = normalizeValue(raw?.value ?? raw);
                      if (newValue !== null) {
                        finalRecord[field] = newValue;
                      }
                    }
                  });

                  const auditPayload = {
                    form: { name: currentForm.slice(4) },
                    action: "CREATE",
                    device: "web",
                    page: "Records",
                    network: "online",
                    ids: [newRecordId],
                    changes,
                    rawData: [finalRecord],
                    isReverted: false,
                    metadata: { importQueueId: id },
                    createdBy: props.user.sub,
                    createdAt: new Date().toISOString(),
                  };
                  trackActivity(auditPayload);
                }
              } catch (e) {
                console.error("Erro ao registrar atividade:", e);
              }

              await sleep(800);
              // Tenta obter os detalhes atualizado da fila
              await importApi.filesHistoryErrorRemove({
                id,
                formName: currentForm,
                errorIds: [checkedRows[0]._id],
                action: "add",
                recordsIds: res.data
              });
              await sleep(400);
              await importApi.filesHistoryById(id).then((items) => {
                setQueue(_.omit(items.message[0], ["errorDocuments"]));
              });
              await sleep(25);
              // Limpa os campos referenciados e lookups
              dispatch(deleteAllRefFieldsFormData());
              // Limpa os campos de retorno
              dispatch(deleteAllFormData());
              // Limpa seleção do registro para edição
              if (recordFormAction === "edit") {
                setCheckedRowsData([]);
              }
              await sleep(25);
              // Fecha o formulário
              setOpenForm(false);
              // Atualiza a tabela
              if (tableRef.current) {
                tableRef.current.reloadAndRest();
              } else {
                window.location.reload();
              }
            })
            .catch((err) => {
              dispatch(
                showNotification("Algo está errado, tente novamente!", "error")
              );
              const errorResponse = err?.response?.data;
              if (errorResponse?.message?.fields) {
                return {
                  success: false,
                  type: 'field',
                  fields: errorResponse.message.fields
                }
              }
            });
        }
        else if (action && action === "multipleEdit") {
          const fields = _.keyBy(props.formData.fields, 'field');
          let ids = checkedRows.map((values) => values._id) || [];
          const currentErrs = _.keyBy(props.formData.queueErrorsMsg.filter(item => ids.includes(item._id)), '_id');
          const rowsData = _.cloneDeep(props.formData.records.filter(item => ids.includes(item._id)));
          const originalRows = _.cloneDeep(rowsData); // Copia profunda para preservar valores originais

          let canUpdate = true;
          const records = [];
          const fieldsWithError = [];

          const fieldsLookUpchildrens = Object.values(fields)
            .filter(value => value.isReadOnly)
            .map(item => item.field)

          for (let i = 0; i < rowsData.length; i++) {
            const record = rowsData[i];
            const entriesData = Object.entries(data);
            for1: for (let j = 0; j < entriesData.length; j++) {
              const [key, value] = entriesData[j];
              if (key === "acceptEmptyValues" || fieldsLookUpchildrens.includes(key)) continue;
              let originValue = "import";
              if (data.acceptEmptyValues) {
                if (currentErrs[record._id]?.[key] !== undefined && checkIfIsEmpty(data[key])) {
                  canUpdate = false;
                  fieldsWithError.push(`O campo ${key} está valores inválidos para inserção`)
                  break for1;
                } else if (checkIfIsEmpty(data[key]) && !!fields[key].requiredField) {
                  originValue = "import";
                  record[key] = record[key];
                } else {
                  originValue = "form";
                  record[key] = data[key]
                }
              } else {
                if (currentErrs[record._id]?.[key] !== undefined && checkIfIsEmpty(data[key])) {
                  canUpdate = false;
                  fieldsWithError.push(`O campo ${key} está valores inválidos para inserção`)
                  break for1;
                } else if (checkIfIsEmpty(data[key])) {
                  if (record[key] === undefined) {
                    originValue = "form";
                    record[key] = data[key];
                  } else {
                    originValue = "import";
                    record[key] = record[key];
                  }
                } else {
                  originValue = "form";
                  record[key] = data[key];
                }
              }

              if (fields[key].fieldtype === 'refLookup') {

                const fieldMap = new Map(props.formData.fields.filter(f => !!f.isReadOnly).map(f => [f._id, f]));
                const refs = fields[key].checkValueIn
                  .map(({ field }) => fieldMap.get(field))
                  .filter(Boolean);


                for (let k = 0; k < refs.length; k++) {
                  const ref = refs[k].field;
                  if (originValue === "form") {
                    record[ref] = data[ref];
                  } else if (originValue === "import") {
                    record[ref] = record[ref];
                  }
                }

              }
            }
            records.push(record);
          }
          if (!canUpdate) {
            dispatch(
              showNotification(_.uniq(fieldsWithError).join(";"), "error")
            );
            return false;
          } else {
            // Envia os registros para adição
            return addRecords({
              formName: currentForm,
              records: records,
              createdBy: props.user.sub,
              acceptEmptyValues: !!data.acceptEmptyValues
            })
              .then(async (res) => {
                if (res instanceof Error || (res && res.isAxiosError) || !res?.data?.length || !res?.data[0]) {
                  notification["error"]({
                    message: "Erro ao salvar registros",
                    description: res?.response?.data?.message || res?.message || "Ocorreu um erro ao salvar os registros. Tente novamente.",
                  });
                  return;
                }

                notification["success"]({
                  message: "Registros",
                  description: res.message,
                });

                // CHANGE TRACKING LOGIC FOR MULTIPLE ADD
                try {
                  const changes = [];
                  const finalRecords = [];
                  const newIds = Array.isArray(res.data) ? res.data : [res.data];

                  originalRows.forEach((row, index) => {
                    const newId = newIds[index];
                    const updatedRow = records[index];

                    Object.keys(updatedRow).forEach((field) => {
                      if (['_id', 'createdAt', 'updatedAt'].includes(field)) return;

                      const oldRaw = row[field];
                      const newRaw = updatedRow[field];

                      const isMulti = Array.isArray(newRaw);

                      const newValue = isMulti
                        ? normalizeMultiValue(newRaw)
                        : normalizeValue(newRaw?.value ?? newRaw);

                      const oldValue = isMulti
                        ? normalizeMultiValue(oldRaw)
                        : normalizeValue(Array.isArray(oldRaw) ? oldRaw[0]?.value : oldRaw);

                      const isEqual = isMulti
                        ? JSON.stringify(newValue) === JSON.stringify(oldValue)
                        : newValue === oldValue;

                      if (!isEqual) {
                        changes.push({
                          id: newId,
                          field,
                          value: isMulti ? newValue.join(', ') : newValue,
                          oldValue: isMulti ? oldValue.join(', ') : oldValue,
                        });
                      }
                    });
                    finalRecords.push(updatedRow);
                  });

                  if (changes.length > 0) {
                    const auditPayload = {
                      form: { name: currentForm.slice(4) },
                      action: "CREATE",
                      device: "web",
                      page: "Records",
                      network: "online",
                      ids: Array.isArray(newIds) ? newIds : [newIds],
                      changes,
                      rawData: Array.isArray(finalRecords) ? finalRecords : [finalRecords],
                      isReverted: false,
                      metadata: { importQueueId: id },
                      createdBy: props.user.sub,
                      createdAt: new Date().toISOString(),
                    };
                    trackActivity(auditPayload);
                  }
                } catch (e) {
                  console.error("Erro ao registrar atividade multipleEdit:", e);
                }

                await sleep(800);
                // Tenta obter os detalhes atualizado da fila
                await importApi.filesHistoryErrorRemove({
                  id,
                  formName: currentForm,
                  errorIds: checkedRows.map(item => item._id),
                  action: "add",
                  recordsIds: res.data
                });
                await sleep(400);
                await importApi.filesHistoryById(id).then((items) => {
                  setQueue(_.omit(items.message[0], ["errorDocuments"]));
                });

                await sleep(25);
                // Limpa os campos referenciados e lookups
                dispatch(deleteAllRefFieldsFormData());
                // Limpa os campos de retorno
                dispatch(deleteAllFormData());
                // Limpa seleção do registro para edição
                if (recordFormAction === "edit") {
                  setCheckedRowsData([]);
                }
                await sleep(25);
                // Fecha o formulário
                setOpenForm(false);
                // Atualiza a tabela
                if (tableRef.current) {
                  tableRef.current.reloadAndRest();
                } else {
                  window.location.reload();
                }
              })
              .catch((err) => {
                dispatch(
                  showNotification("Algo está errado, tente novamente!", "error")
                );
                const errorResponse = err?.response?.data;
                if (errorResponse?.message?.fields) {
                  return {
                    success: false,
                    type: 'field',
                    fields: errorResponse.message.fields
                  }
                }
                else if (errorResponse?.errors?.length > 0) {
                   const fields = _(errorResponse.errors)
                   .flatMap(e => e.error?.fields || [])
                   .uniqBy(f => `${f.fieldName}__${f.errorField}`)
                   .value();
                  return {
                    success: false,
                    type: 'field',
                    fields
                  }
                }
              });
          }

        } else {
          dispatch(
            showNotification(
              "Algo está errado, atualize a página e tente novamente!",
              "error"
            )
          );
        }
      }
    } else {
      // Notificação de erro se nenhum campo for preenchido
      dispatch(
        showNotification(
          "Você precisa preencher algum dos campos para confirmar o envio.",
          "error"
        )
      );
    }
  };

  /**
   * Formata tempo decorrido da fila
   * @returns
   * @param start
   * @param end
   */
  function formatTime(start, end = null) {
    const startTime = moment(start);
    const endTime = end ? moment(end) : moment();

    const duration = moment.duration(endTime.diff(startTime));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    let result = '';

    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0) {
      result += `${minutes}m `;
    }
    if (seconds > 0 || result === '') {
      result += `${seconds}s`; // Adiciona "s" se segundos existirem ou se não houver outras unidades
    }

    return result.trim();
  }


  const [isReverting, setIsReverting] = useState(false);

  const handleRollbackImport = async () => {
    setIsReverting(true);
    try {
      const { status, data } = await importApi.rollbackImport(id, props.user.sub);

      if (status === 200) {
        try {
          const activitiesRes = await activityAPI.list({ importQueueId: id, pageSize: 100 });
          const result = activitiesRes?.data;
          let activities = [];
          if (Array.isArray(result)) activities = result;
          else if (result?.data && Array.isArray(result.data)) activities = result.data;
          else if (Array.isArray(result?.message)) activities = result.message;
          await Promise.all(activities.map(a => activityAPI.revertFlag(a.id)));
        } catch (e) {
          // non-critical: don't block the revert flow
        }

        notification.success({
          message: "A reversão da importação foi feita com sucesso!",
          onClose: () => navigate(`/formularios/registros/${name}`),
          duration: 3,
        });
      } else {
        setIsReverting(false);
        notification.error({
          message: "A reversão falhou",
          description: typeof data === "string" ? data : JSON.stringify(data, null, 2),
        });
      }
    } catch (error) {
      setIsReverting(false);
      notification.error({
        message: "Erro ao reverter importação",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  };


  const showModalConfirmRollbackImport = () => {
    Modal.confirm({
      title: "Reversão",
      content: <><p>Todos os dados dessa importação serão <b>removidos</b>, incluindo os dados com <b>erros </b>e os já <b>importados com sucesso</b></p><p> Continuar com essa ação?</p></>,
      okText: "Sim",
      cancelText: "Não",
      onOk: () => handleRollbackImport()
    })
  }

  return (
    <Spin spinning={isReverting} tip="Revertendo importação...">
      <Layout>
        <LayoutTopSide>
          <TopBar />
          <Layout style={{ overflow: "hidden" }}>
            <DrawerSider />
            <Layout style={{ padding: "0 53px 53px", height: "100%", overflowY: "auto" }}>
              <PageHeader
                style={{ paddingLeft: 0, margin: "16px 0 0 0" }}
                title={`Status da importação${currentForm ? ` — ${currentForm.replace("obj_", "")}` : ""}`}
                onBack={() => window.history.back()}
                subTitle="Acompanhe o progresso dos registros importados para o seu formulário."
                breadcrumb={
                  <Breadcrumb>
                    {buildRoutes(currentForm ? currentForm.replace("obj_", "") : "").map((a) =>
                      a.hasOwnProperty("href") ? (
                        <Breadcrumb.Item key={Math.random()}>
                          <a href onClick={() => navigate(a.href)}>
                            {a.breadcrumbName}
                          </a>
                        </Breadcrumb.Item>
                      ) : (
                        <Breadcrumb.Item>{a.breadcrumbName}</Breadcrumb.Item>
                      )
                    )}
                  </Breadcrumb>
                }
                extra={
                  <Button
                    disabled={!queue || (queue.status !== 'finished' && queue.status !== 'error' && queue.status !== 'failed') || queue?.isReverted}
                    danger
                    onClick={() => showModalConfirmRollbackImport()}>
                    {queue?.isReverted ? "Revertido" : "Reverter importação"}
                  </Button>
                }
              />
              <Row gutter={[16, 16]}>
                <Col xs={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Registros na fila"
                      value={data.total}
                      prefix={<FileSyncOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Registros com erro"
                      value={Math.max(0, data.errorCount - (queue?.clearDocs?.length || 0))}
                      valueStyle={{
                        color: "#cf1322",
                      }}
                      prefix={<FileExclamationOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Registros salvos"
                      value={data.successCount + (queue?.clearDocs?.length || 0)}
                      valueStyle={{
                        color: "#3f8600",
                      }}
                      prefix={<FileProtectOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Tempo decorrido"
                      value={timeDiff}
                      prefix={<ClockCircleOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
              <Card
                style={{
                  marginTop: "16px",
                }}
              >
                {(queue && (queue?.status === "error" || (queue?.status === "finished" && hasErrors))) ? (
                  <ConfigProvider locale={ptBRIntl}>
                    <div className="table-wrapper">
                      {/* Todos os registros */}
                      <ProTable
                        actionRef={tableRef}
                        rowKey="_id"
                        type="table"
                        pagination={defaultTablePagination}
                        request={async (params = {}, sorter, filter) => {
                          // Dispara a ação de busca dos dados do formulário
                          dispatch(
                            fetchImportFormData(
                              currentForm, // nome do formuláro
                              id // id da fila
                            )
                          );

                          // Aguarda um curto período de tempo para garantir que os dados estejam disponíveis
                          await new Promise((resolve) => setTimeout(resolve, 500));

                          // Constroi dados para tabela com props.formData.records
                          return buildTableData(props.formData.records);
                        }}
                        columns={columns}
                        dataSource={tableRows}
                        toolbar={null}
                        headerTitle={`${props.formData.total} ${props.formData.total === 0 ? "registro" : "registros"
                          } ${props.formData.total === 0 ? "encontrado" : "encontrados"
                          }`}
                        tooltip="Resultados encontrado no formulário que você selecionou"
                        options={{
                          reload: true,
                        }}
                        tableAlertRender={({
                          selectedRowKeys,
                          selectedRows,
                          onCleanSelected,
                        }) => {
                          setCheckedRows(selectedRows);
                          return (
                            <Space size={24}>
                              <span>
                                Selecionado {selectedRowKeys.length} registros
                                <a
                                  style={{ marginInlineStart: 8 }}
                                  onClick={() => {
                                    onCleanSelected();
                                  }}
                                >
                                  cancelar seleção
                                </a>
                              </span>
                            </Space>
                          );
                        }}
                        tableAlertOptionRender={() => {
                          return (
                            <Space size={16}>
                              <a
                                href={() => false}
                                onClick={async () => {
                                  // Limpa os campos
                                  recordFormRef.current?.resetFields();
                                  // Verifica se é seleção única
                                  if (checkedRows.length === 1) {
                                    // Tenta obter uuid do registro
                                    const recordId = checkedRows[0]._id;
                                    // Ação (CRUD)
                                    setRecordFormAction("edit");
                                    // Tenta obter o registro
                                    const nativeSelectedRecord = _.filter(
                                      props.formData.records,
                                      { _id: recordId }
                                    );
                                    const selectedRecordMessage = nativeSelectedRecord[0] || {};
                                    // Verifica se encontrou o registro selecionado
                                    if (selectedRecordMessage) {
                                      // Salva o registro em cache para ser utilizado em mountRecords()
                                      setCheckedRowsData(selectedRecordMessage);
                                    }
                                    // Procura por campos referenciados e lookups
                                    const onlyRefLkpFields =
                                      props.formData.fields.filter(
                                        (e) =>
                                          e.fieldtype === "refLookup" ||
                                          e.fieldtype === "refUniqueSelection" ||
                                          e.fieldtype === "refMultipleSelection"
                                      );
                                    const onlyLkpFields =
                                      props.formData.fields.filter(
                                        (e) => e.fieldtype === "refLookup"
                                      ) || [];
                                    // Remove campos referenciados e lookups
                                    const withoutRefLkp =
                                      _.omit(
                                        selectedRecordMessage,
                                        _.map(onlyRefLkpFields, "field")
                                      ) || {};
                                    // Verifica se registro não foi excluido
                                    if (
                                      selectedRecordMessage &&
                                      !_.isEmpty(selectedRecordMessage)
                                    ) {
                                      const onlyRefLkp = _.pick(
                                        selectedRecordMessage,
                                        _.map(onlyRefLkpFields, "field")
                                      );
                                      const transformedOnlyRefLkp = {};
                                      // Intera sobre os campos referenciados e lookups
                                      for (const key in onlyRefLkp) {
                                        if (onlyRefLkp.hasOwnProperty(key)) {
                                          if (onlyRefLkp[key].length > 0) {
                                            const genId = `${onlyRefLkp[key]
                                              .map((obj) => obj._id)
                                              .join(",")}_rng@record_${uuid()}`;
                                            const currentValue = onlyRefLkp[key]
                                              .map((obj) => obj.value)
                                              .join(", ");
                                            // Verifica se existe valor no campo
                                            if (!genId.startsWith("_r")) {
                                              // Extrai as propriedades _id e value, une com vírgulas e armazena
                                              transformedOnlyRefLkp[key] = [
                                                {
                                                  _id: genId,
                                                  value: currentValue,
                                                },
                                              ];
                                              dispatch(
                                                addRefFieldsFormData(key, [
                                                  {
                                                    value: genId,
                                                    label: currentValue,
                                                  },
                                                ])
                                              );
                                              // Tenta preencher o formulário com os registros de campos referenciados e lookups
                                              recordFormRef.current?.setFieldsValue({
                                                [key]: genId,
                                              });
                                            }
                                          }
                                        }
                                      }
                                      // Prepara o cache para os campos referenciados e lookups
                                      setCheckedRowsRef(transformedOnlyRefLkp);
                                      // Prepara os dados para ser preenchido na tabela
                                      let recordForEdit = getFieldValueForTable(
                                        [withoutRefLkp],
                                        _.concat(
                                          props.formData.fields,
                                          props.formData.refFields
                                        ).filter((y) => y.isReadOnly === undefined),
                                        true
                                      );
                                      // Verifica se existe campos de lookups
                                      if (
                                        onlyLkpFields &&
                                        onlyLkpFields.length > 0
                                      ) {
                                        onlyLkpFields.forEach(async (obj) => {
                                          if (
                                            selectedRecordMessage[obj.field] &&
                                            selectedRecordMessage[obj.field]
                                              .length > 0
                                          ) {
                                            if (
                                              selectedRecordMessage[obj.field][0]
                                                ._id !== ""
                                            ) {
                                              // Obtém os dados do campo de retorno e preenche o formulário
                                              await getLookupData({
                                                tableRef: recordFormRef,
                                                selectedField: obj,
                                                selectedOpts: `${selectedRecordMessage[
                                                  obj.field
                                                ][0]._id
                                                  }_rng@record_${uuid()}`,
                                                dispatch,
                                              });
                                            }
                                          }
                                        });
                                      }
                                      // Tenta preencher os campos simples
                                      recordFormRef.current?.setFieldsValue(
                                        // Remove campos referenciados e lookups
                                        _.omit(
                                          recordForEdit,
                                          _.map(onlyRefLkpFields, "field")
                                        )
                                      );
                                    }
                                    // Espera alguns instantes
                                    await sleep(700);
                                    // Abre formulário
                                    setOpenForm(true);
                                  } else {
                                    await sleep(700);
                                    setRecordFormAction("multipleEdit");
                                    setOpenForm(true);
                                  }
                                }}
                              >
                                Editar
                              </a>
                              <a href={() => false} onClick={deleteConfirm}>
                                Excluir
                              </a>
                            </Space>
                          );
                        }}
                        locale={{
                          emptyText: (
                            <Empty
                              description={
                                errorsLoading
                                  ? "Carregando registros com erro..."
                                  : hasErrors
                                    ? "Nenhum registro com erro encontrado."
                                    : "Tudo ok, nenhum erro encontrado."
                              }
                            />
                          ),
                          triggerDesc: "Ordem decrescente",
                          triggerAsc: "Ordem crescente",
                        }}
                        dateFormatter={"number"}
                        scroll={{ x: 'max-content' }}
                        style={{ padding: 0 }}
                        rowSelection={{}}
                        search={false}
                        cardBordered={true}
                      />
                      {/* Formulário */}
                      <BetaSchemaForm
                        formRef={recordFormRef}
                        open={openForm}
                        layoutType={"ModalForm"}
                        modalProps={{
                          forceRender: true,
                          onCancel: async () => {
                            // limpa os erro
                            setValidationErrors({});

                            // Limpa os campos referenciados e lookups
                            dispatch(deleteAllRefFieldsFormData());
                            // Limpa os campos de retorno
                            dispatch(deleteAllFormData());
                            if (recordFormAction === "edit" || recordFormAction === "multipleEdit") {
                              // Limpa seleção do registro para edição
                              setCheckedRowsData([]);
                            }
                            // Fecha o formulário
                            setOpenForm(false);
                            setRecordFormAction('add')
                          },
                          okText: "Salvar registro",
                        }}
                        submitter={{
                          render: (fProps, defaultDoms) => {
                            if (checkedRows.length === 1) {
                              const cacheRecordId = checkedRows[0]._id;
                              // Tenta obter os erros de cada campo
                              const currentErr = _.find(
                                props.formData.queueErrorsMsg,
                                {
                                  _id: cacheRecordId,
                                }
                              );
                              // Espera os campos renderizar antes de alterar cada campo
                              if (
                                cacheRecordId &&
                                currentErr &&
                                !_.isEmpty(currentErr) &&
                                !_.isEmpty(fProps.form.getFieldsValue())
                              ) {
                                // Remove os campos _id, createdAt e updatedAt
                                const currentErrCopy = { ...currentErr };
                                delete currentErrCopy._id;
                                delete currentErrCopy.createdAt;
                                delete currentErrCopy.updatedAt;
                                // Obtém todos os campos restantes do registro
                                const keys = Object.keys(
                                  _.omitBy(currentErrCopy, _.isNil)
                                );
                                // Verifica se existe campos
                                if (keys && keys.length > 0) {
                                  // caso contenha erros de validacao (foi encaminhado para api, mas ainda nao passou na validação)
                                  if (!_.isEmpty(validationErrors)) {
                                    orderedFormFields.forEach((f) => {
                                      fProps.form.setFields([{name: f.field, errors: []}])
                                    })
                                      // seta os novos erros
                                    Object.entries(validationErrors).forEach(([key, msg]) => {
                                      fProps.form.setFields([{ name: key, errors: Array.isArray(msg) ? msg : [msg] }]);
                                    })
                                  } else {
                                    // caso seja a primeira abertura, mostra os erros gerados pela importação. 
                                    keys.map((key) => {
                                      fProps.form.setFields([
                                        {
                                          name: key,
                                          errors: [currentErrCopy[key]],
                                        },
                                      ]);
                                    });
                                  }
                                }
                              }
                            } else {
                              let ids = checkedRows.map((values) => values._id) || [];
                              const currentErrs = props.formData.queueErrorsMsg.filter(item => ids.includes(item._id));
                              if (currentErrs.length > 0) {
                                currentErrs.forEach(currentErr => {
                                  const currentErrCopy = { ...currentErr };
                                  delete currentErrCopy._id;
                                  delete currentErrCopy.createdAt;
                                  delete currentErrCopy.updatedAt;

                                  const keys = Object.keys(_.omitBy(currentErrCopy, _.isNil));

                                  if (keys && keys.length > 0) {
                                    if (!_.isEmpty(validationErrors)) {
                                      // limpa os erros de importação
                                      orderedFormFields.forEach((f) => {
                                        fProps.form.setFields([{name: f.field, errors: []}])
                                      });
                                      // seta os erros de validação
                                      Object.entries(validationErrors).forEach(([key, msg]) => {
                                        fProps.form.setFields([{ name: key, errors: Array.isArray(msg) ? msg : [msg] }]);
                                      });
                                    } else {
                                      // Seta os erros originais do registro
                                      keys.forEach((key) => {
                                        fProps.form.setFields([{ name: key, errors: [currentErrCopy[key]] }]);
                                      });
                                    }
                                  }
                                });
                              }
                            }
                            return [
                              ...defaultDoms,
                            ];
                          }
                        }}
                        columns={recordFormColumnsFinal}
                        labelCol={{
                          span: 24,
                        }}
                        onFinish={async (formValues) => {
                          // Mantém os dados do formulário em cache
                          let submitedData = formValues;
                          // Filtra campos por tipo
                          const onlyFieldsName =
                            _.map(
                              props.formData.fields.filter(
                                (e) =>
                                  e.fieldtype === "Text" ||
                                  e.fieldtype === "Numeric" ||
                                  e.fieldtype === "Date" ||
                                  e.fieldtype === "Time" ||
                                  e.fieldtype === "uniqueSelection" ||
                                  e.fieldtype === "multipleSelection" ||
                                  e.fieldtype === "refUniqueSelection" ||
                                  e.fieldtype === "refMultipleSelection" ||
                                  e.fieldtype === "refLookup"
                              ),
                              "field"
                            ) || [];
                          // Verifica se existe campos no formulário
                          if (onlyFieldsName && onlyFieldsName.length > 0) {
                            // Se encontrar tenta fixar erro do onClear()
                            onlyFieldsName.forEach((item) => {
                              // Verifica se o campo não existe
                              if (!submitedData.hasOwnProperty(item)) {
                                // Adiciona campo com valor vazio
                                submitedData[item] = "";
                              }
                            });
                          }
                          // Tenta converter os dados para API
                          const result = await mountRecords(
                            currentForm,
                            submitedData,
                            props.formData.fields,
                            props.formData.refValues,
                            props.formData.lkpValues,
                            checkedRowsData,
                            recordFormAction
                          );
                          // Remove campos vazios
                          var finalResult = removeUndefined(_.assign({}, ...result)) || {};
                          // Tenta enviar dados para API
                          const resp = await trySaveRecords(finalResult, recordFormAction);
                          
                          if (!resp?.success && resp?.type === 'field') {
                            //
                            const newValidationErrors = {};
                            resp.fields.forEach(field => {
                              if (!newValidationErrors[field.fieldName]) {
                                newValidationErrors[field.fieldName] = [];
                              }
                              newValidationErrors[field.fieldName].push(field.errorField);
                            });
                            setValidationErrors(newValidationErrors);
                            recordFormRef.current?.scrollToField(resp.fields[0]?.name);
                            return;
                          }
                          
                          if (_.isBoolean(resp)) {
                            return resp;
                          }
                        }}
                      />
                    </div>
                  </ConfigProvider>
                ) : null}
                {(queue?.status === "finished" && !hasErrors) && (
                  <Card style={{ height: "100vh" }}>
                    <Result
                      status="success"
                      title="Importação concluída!"
                      subTitle="Todos os dados foram processados e armazenados com sucesso."
                    />
                  </Card>
                )}
                {(queue && (queue.status !== "finished" && queue.status !== "error")) && (
                  <Card style={{ height: "100vh", display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '100px' }}>
                    {queueInfo && queueInfo.isQueueFull && (queue.status === 'queue' || queue.status === 'pending') && (
                      <Alert
                        message="Sua importação está na fila de espera"
                        description={`Existem ${queueInfo.activeCount} importações em andamento no momento. Sua importação será processada automaticamente assim que houver disponibilidade.${queueInfo.waitingCount > 0 ? ` (${queueInfo.waitingCount} importação(ões) aguardando na fila)` : ''}`}
                        type="info"
                        showIcon
                        style={{ marginBottom: 24, width: '80%' }}
                      />
                    )}
                    <div style={{ width: '80%', textAlign: 'center' }}>
                      <Progress
                        percent={
                          (data.total + data.successCount + data.errorCount) > 0
                            ? Number((((data.successCount + data.errorCount) / (data.total + data.successCount + data.errorCount)) * 100).toFixed(1))
                            : 0
                        }
                        status="active"
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                      />
                      <div style={{ marginTop: 20 }}>
                        <h3>{(queue.status === 'queue' || queue.status === 'pending') ? 'Aguardando na fila...' : 'Importando registros...'}</h3>
                        <p>{(queue.status === 'queue' || queue.status === 'pending') ? 'Sua importação será iniciada em breve.' : 'Aguarde enquanto processamos seus dados.'}</p>
                      </div>
                    </div>
                  </Card>
                )}
              </Card>
            </Layout>
            <Notification />
          </Layout>
        </LayoutTopSide>
      </Layout>
    </Spin>
  );
}

const mapStateToProps = (all) => {
  return {
    user: all.session.user,
    formData: all.records,
  };
};

export default connect(mapStateToProps)(ImportStatus);
